import { useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchFileBlob } from '../filesApi';

// eslint-disable-next-line react-refresh/only-export-components -- shared helper; extracting it to its own module is logged in the backlog
export const isImageFile = (file) => (file?.mimeType ?? '').startsWith('image/');

/**
 * Authenticated image thumbnail for list/grid rows. The API uses Bearer auth,
 * so a plain <img src> can't reach it — fetch the bytes through axios and show
 * an object URL instead. Blobs are cached per file+version in react-query so
 * switching between list and grid (or re-visiting a folder) doesn't refetch.
 * Renders `fallback` (the usual file icon) while loading or on error.
 */
export default function ImageThumb({ file, className, fallback = null }) {
  const { data: blob } = useQuery({
    queryKey: ['file-thumb', file.id, file.updatedAt ?? null],
    queryFn: () => fetchFileBlob(file.id),
    staleTime: Infinity,
    gcTime: 10 * 60 * 1000,
    retry: false,
    enabled: !file.metadata?.legacy?.contentStatus || file.metadata.legacy.contentStatus.startsWith('available'),
  });

  const url = useMemo(() => (blob ? URL.createObjectURL(blob) : null), [blob]);
  useEffect(() => {
    return () => {
      if (url) URL.revokeObjectURL(url);
    };
  }, [url]);

  if (!url) return fallback;
  return <img src={url} alt={file.name} loading="lazy" className={className} draggable={false} />;
}
